/**
 * Admin navigation groups — ONE source of truth shared by AdminShell's
 * sidebar and the Pages editor's overlay drawer, so the two navs never
 * drift.
 *
 * Builder-v3 note: the Pages editor edits page SECTIONS and, in Lists
 * mode, the RECORDS record-bound sections auto-pull (Drip menu, Services,
 * FAQs, Program steps, Testimonials). The list entries are deep links into
 * that editor (`/pages?list=…`).
 *
 * Settings + Team are PINNED to the bottom (Shopify-style) instead of a
 * collapsible group — a longer Content group once pushed a collapsed
 * "Settings" group below the fold on laptop screens.
 */

import { NavLink } from 'react-router-dom';
import { useReviewCount } from '~/lib/useReview';

interface NavItem {
  to: string;
  label: string;
  icon?: React.ReactNode;
  /** 'review' → the open change-request count rides on the link. */
  badge?: 'review';
}
export interface NavGroup {
  label: string;
  icon: React.ReactNode;
  items: NavItem[];
  defaultOpen?: boolean;
}

const iconProps = {
  width: 14,
  height: 14,
  viewBox: '0 0 14 14',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const OperationsIcon = () => (
  <svg {...iconProps} aria-hidden>
    <rect x="2" y="3.5" width="10" height="8.5" rx="1.4" />
    <line x1="2" y1="6.2" x2="12" y2="6.2" />
    <line x1="4.5" y1="2" x2="4.5" y2="4.5" />
    <line x1="9.5" y1="2" x2="9.5" y2="4.5" />
  </svg>
);
const PeopleIcon = () => (
  <svg {...iconProps} aria-hidden>
    <circle cx="5" cy="5" r="2" />
    <path d="M1.5 12c.4-2 1.8-3 3.5-3s3.1 1 3.5 3" />
    <circle cx="10" cy="5.8" r="1.6" />
    <path d="M8.4 11.4c.4-1.1 1.2-1.8 2.1-1.8 1.1 0 2 .9 2 2" />
  </svg>
);
const ContentIcon = () => (
  <svg {...iconProps} aria-hidden>
    <path d="M2 11.7L3 8.7l6.5-6.5a1.2 1.2 0 0 1 1.7 0l1 1a1.2 1.2 0 0 1 0 1.7L5.6 11.4 2 11.7z" />
    <path d="M8.7 3l2.5 2.5" />
  </svg>
);
const BusinessIcon = () => (
  <svg {...iconProps} aria-hidden>
    <polyline points="2,10 5.2,6.8 8.2,9 12,4.5" />
    <polyline points="9,4.5 12,4.5 12,7.5" />
  </svg>
);
const SettingsIcon = () => (
  <svg {...iconProps} aria-hidden>
    <line x1="2" y1="4.2" x2="12" y2="4.2" />
    <line x1="2" y1="9.8" x2="12" y2="9.8" />
    <circle cx="5" cy="4.2" r="1.5" fill="var(--primary-deep)" />
    <circle cx="9.5" cy="9.8" r="1.5" fill="var(--primary-deep)" />
  </svg>
);
const TeamIcon = () => (
  <svg {...iconProps} aria-hidden>
    <rect x="1.8" y="3" width="10.4" height="8" rx="1.4" />
    <circle cx="5" cy="6.3" r="1.2" />
    <path d="M3.3 9.3c.3-.8 1-1.3 1.7-1.3s1.4.5 1.7 1.3" />
    <line x1="8.2" y1="5.9" x2="10.6" y2="5.9" />
    <line x1="8.2" y1="7.9" x2="10" y2="7.9" />
  </svg>
);

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Operations',
    icon: <OperationsIcon />,
    defaultOpen: true,
    items: [
      { to: '/today',    label: 'Today' },
      { to: '/calendar', label: 'Calendar' },
      { to: '/bookings', label: 'Bookings' },
    ],
  },
  {
    label: 'People',
    icon: <PeopleIcon />,
    defaultOpen: true,
    items: [
      { to: '/clients', label: 'Clients' },
      { to: '/glp1',    label: 'GLP-1 Program' },
    ],
  },
  {
    label: 'Content',
    icon: <ContentIcon />,
    defaultOpen: true,
    items: [
      { to: '/pages',                   label: 'Pages' },
      { to: '/review',                  label: 'Change requests', badge: 'review' },
      { to: '/pages?list=drips',        label: 'Drip menu' },
      { to: '/pages?list=services',     label: 'Services' },
      { to: '/pages?list=faqs',         label: 'FAQs' },
      { to: '/pages?list=steps',        label: 'Program steps' },
      { to: '/pages?list=testimonials', label: 'Testimonials' },
      { to: '/blocks',                  label: 'Shared blocks' },
      { to: '/navigation',              label: 'Nav & footer' },
      { to: '/app',                     label: 'App lessons' },
    ],
  },
  {
    label: 'Business',
    icon: <BusinessIcon />,
    defaultOpen: true,
    items: [
      { to: '/money', label: 'Money' },
    ],
  },
];

/** Open change-request count, drawn on the nav item that asks for it. Only
 * ever mounted for that one item, so the count is fetched once. */
export function ReviewBadge() {
  const n = useReviewCount();
  if (!n) return null;
  return (
    <span className="rv-navbadge" aria-label={`${n} open`}>
      {n}
    </span>
  );
}

/** Groups for a given role. Same list for every role today — server-side
 * gates protect the sensitive routes; hiding nav items would just make
 * the two navs feel inconsistent. */
export function navGroupsFor(_role: string | undefined): NavGroup[] {
  return NAV_GROUPS;
}

/** Always-visible bottom links — never inside a collapsible group. */
export const PINNED_NAV: NavItem[] = [
  { to: '/settings', label: 'Settings', icon: <SettingsIcon /> },
  { to: '/users', label: 'Team', icon: <TeamIcon /> },
];

/** The pinned links, shared by AdminShell's sidebar footer and the Pages
 * editor's drawer. */
export function PinnedNav({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <div className="admin-sidebar__pinned">
      {PINNED_NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={onNavigate}
          className={({ isActive }) => `admin-sidebar__link${isActive ? ' is-active' : ''}`}
        >
          <span className="admin-sidebar__group-icon">{item.icon}</span>
          {item.label}
        </NavLink>
      ))}
    </div>
  );
}
